import { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import { AccessLevelIcon } from '@dbt-labs/dbt-dag';
import {
  TrustSignals,
  TrustSignalsBadgeContainer,
  trustSignalsSupportedResourceTypes,
} from '@dbt-labs/metadata-shared';
import { Tooltip } from '@dbt-labs/sourdough';

import { AccessLevel } from '@/__generated__/graphql';
import { useGetLinkMapping } from '@/hooks/useGetLinkMapping';
import { ParsedUniqueId } from '@/typings/parseUniqueId';
import { UrlEnvironmentType } from '@/typings/url';

interface PanelTitleProps {
  access: AccessLevel | undefined;
  name: string | undefined;
  trustSignals: TrustSignals;
  uniqueId: ParsedUniqueId;
  environmentType?: UrlEnvironmentType;
  projectId?: number;
  className?: string;
}

export const ResourcePanelTitle: FC<PanelTitleProps> = ({
  access,
  environmentType,
  name,
  projectId,
  trustSignals,
  uniqueId,
  className,
}) => {
  const dbtCorePackageName = uniqueId.packageName;

  const showAccessIcon = !!access;
  const showTrustSignalsBadge = trustSignalsSupportedResourceTypes.includes(
    uniqueId.resourceType,
  );

  const getLinkMapping = useGetLinkMapping();

  if (uniqueId.resourceType === 'model' && projectId && environmentType) {
    const linkMappings = getLinkMapping({
      healthIssues: trustSignals.healthIssues ?? [],
      projectId: projectId,
      environmentType: environmentType,
      uniqueId: uniqueId.raw,
    });

    if (linkMappings) {
      // eslint-disable-next-line react-compiler/react-compiler
      trustSignals.additionalMetadata = {
        ...trustSignals.additionalMetadata,
        linkMapping: linkMappings,
      };
    }
  }
  return (
    <div className={twMerge('w-full', className)}>
      <div className="w-full overflow-hidden text-xl font-medium">
        <Tooltip displayOnlyWhenTruncated content={name} placement="top-end">
          {(ref) => (
            <div ref={ref} className="flex items-center truncate">
              {showAccessIcon && (
                <span>
                  <AccessLevelIcon
                    size="md"
                    access={access}
                    tooltipConfig={{ placement: 'bottom' }}
                    iconClassName="h-6 w-6"
                  />
                </span>
              )}
              <span className={`${showAccessIcon ? 'ml-1' : ''}`}>{name}</span>
              {showTrustSignalsBadge && (
                <span>
                  <TrustSignalsBadgeContainer
                    trustSignals={trustSignals}
                    resourceType={uniqueId.resourceType}
                    className="ml-2 mt-1"
                  />
                </span>
              )}
            </div>
          )}
        </Tooltip>
      </div>
      <div className="mb-4 mt-1 text-xs font-normal">{dbtCorePackageName}</div>
    </div>
  );
};
